import { useCallback, useContext, useEffect, useState, memo } from "react";
import { X, Dot, ChevronLeft, ChevronRight } from "lucide-react";
import { Box, IconButton, Typography } from "@mui/material";
import { addMonths, format, subMonths } from "date-fns";
import { AnimatePresence, motion } from "framer-motion";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { useDispatch } from "react-redux";
import es from "date-fns/locale/es";
import { Dispatch } from "redux";

// U T I L S
import {
	DayPickerContext,
	variantsBigCalendar,
	variantsMonths,
	variantsSmallCalendar,
} from "./CalendarUtils";
import { setViewFullCalendar } from "../../store/reducer";
import { getHeaders, URI_API } from "../../utils/config";
import capitalize from "../../utils/capitalize";

// A S S E T S
import RowVariant from "./RowVariant";
import "./Calendar.scss";
import Row from "./Row";
import { AssistanceAdjustContext } from "../AssistanceAdjust/AssistanceAdjust";

function CustomCaption() {
	return <></>;
}

function Calendar({ viewFullCalendar }: { viewFullCalendar: boolean }) {
	const [nurseCount, setNurseCount] = useState<any>([]);
	const [monthsAlreadyFetch, setMonthsAlreadyFetch] = useState<Set<string>>(new Set());

	// const [selected, setSelected] = useState<Date>(new Date());
	const [month, setMonth] = useState<Date>(new Date());
	const [direction, setDirection] = useState<"left" | "right">("left");

	const { setSelected, selected } = useContext(AssistanceAdjustContext);

	const dispatch: Dispatch<any> = useDispatch();

	const handleNextMonth = useCallback(() => {
		setDirection("right");
		setMonth((prev) => addMonths(prev, 1));
	}, []);

	const handlePrevMonth = useCallback(() => {
		setDirection("left");
		setMonth((prev) => subMonths(prev, 1));
	}, []);

	const _unmountComponent = () => {
		dispatch(setViewFullCalendar(false));
	};

	useEffect(() => {
		let date = new Date();
		date.setDate(1);
		date.setHours(1, 0, 0, 0);

		// Obtenemos la información del mes actual, y de un mes antes y uno después
		[date, subMonths(date, 1), addMonths(date, 1)].map((date: Date) =>
			getNursesByDayMonth(date),
		);

		return _unmountComponent;
	}, []);

	useEffect(() => {
		let newMonth: Date = month;
		newMonth.setDate(1);
		newMonth.setHours(1, 0, 0, 0);

		getNursesByDayMonth(newMonth);
	}, [month]);

	const getNursesByDayMonth = async (date: Date): Promise<void> => {
		try {
			const year = date.getFullYear();
			const month = String(date.getMonth() + 1).padStart(2, "0");
			const year_month = `${year}-${month}`;

			if (monthsAlreadyFetch.has(year_month)) return;

			const response = await fetch(
				`${URI_API}/nursing_calendar_month/${year_month}`,
				getHeaders(),
			);
			const body = await response.json();

			const nursesByDay = body.reduce((acc: any, curr: any) => {
				let date_key = curr["date"];
				if (!acc[date_key]) acc[date_key] = [];
				acc[date_key].push({
					schedule: curr["schedule"],
					value: curr["value"],
				});
				return acc;
			}, {});

			setMonthsAlreadyFetch((prev) => new Set(prev).add(year_month));
			setNurseCount((prev: any) => ({ ...prev, ...nursesByDay }));
		} catch (error) {
			console.error("Error fetching nurse data:", error);
		}
	};

	return (
		<Box className="calendar">
			<Box className="calendar__header">
				<Box className="calendar__header__icons">
					<IconButton
						onClick={handlePrevMonth}
						className="calendar__header__icon"
					>
						<ChevronLeft className="calendar__header__icon--left" />
					</IconButton>

					<Dot className="calendar__header__icon--dark" />
					<IconButton
						onClick={handleNextMonth}
						className="calendar__header__icon"
					>
						<ChevronRight className="calendar__header__icon--right" />
					</IconButton>
				</Box>
				<AnimatePresence mode="wait">
					<motion.div
						key={month.getMonth()}
						initial={{ opacity: 0, zIndex: 1 }}
						animate={{ opacity: 1, zIndex: 0 }}
						exit={{ opacity: 0, zIndex: 1 }}
						transition={{ duration: 0.1 }}
						style={{
							width: "100%",
						}}
					>
						<Typography className="calendar__header__title">
							{capitalize(format(month, "MMMM yyyy", { locale: es }))}
						</Typography>
					</motion.div>
				</AnimatePresence>

				<IconButton onClick={() => dispatch(setViewFullCalendar(!viewFullCalendar))}>
					<X />
				</IconButton>
			</Box>

			<motion.div
				animate={viewFullCalendar ? { height: "3.9rem" } : { height: "auto" }}
				transition={{
					duration: 0.6,
					ease: "easeIn",
				}}
				className="calendar__body"
			>
				<Box className="calendar__body__days">
					<Typography>DOM</Typography>
					<Typography>LUN</Typography>
					<Typography>MAR</Typography>
					<Typography>MIÉ</Typography>
					<Typography>JUE</Typography>
					<Typography>VIE</Typography>
					<Typography>SÁB</Typography>
				</Box>

				{/* Small Calendar */}
				<AnimatePresence initial={false}>
					{!viewFullCalendar && (
						<motion.div
							className="calendar__body__dates"
							transition={{ duration: 0.6, ease: "easeIn" }}
							variants={variantsSmallCalendar}
							initial="initial"
							animate="visible"
							exit="exit"
						>
							<AnimatePresence
								mode="popLayout"
								initial={false}
								custom={direction}
							>
								<motion.div
									key={`${month.getMonth()}-${direction}`}
									custom={direction}
									variants={variantsMonths}
									initial="initial"
									animate="visible"
									exit="exit"
									transition={{ duration: 0.7, ease: "easeIn" }}
									style={{ width: "100%", height: "auto" }}
								>
									<DayPickerContext.Provider
										value={{ selected, setSelected, nurseCount }}
									>
										<DayPicker
											mode="single"
											required={true}
											locale={{
												...es,
												options: { ...es.options, weekStartsOn: 0 },
											}}
											month={month}
											onMonthChange={setMonth}
											selected={selected}
											onSelect={setSelected}
											hideNavigation={true}
											hideWeekdays
											showOutsideDays
											fixedWeeks
											components={{
												CaptionLabel: CustomCaption,
												Weeks: Row,
											}}
										/>
									</DayPickerContext.Provider>
								</motion.div>
							</AnimatePresence>
						</motion.div>
					)}
				</AnimatePresence>
			</motion.div>

			{/* Big Calendar */}
			<AnimatePresence>
				{viewFullCalendar && (
					<motion.div
						className="calendar__variant__dates"
						transition={{
							duration: 0.7,
							ease: "easeIn",
						}}
						variants={variantsBigCalendar}
						initial="initial"
						animate="visible"
						exit="exit"
					>
						<AnimatePresence
							mode="popLayout"
							initial={false}
							custom={direction}
						>
							<motion.div
								key={`${month.getMonth()}-${direction}`}
								custom={direction}
								variants={variantsMonths}
								initial="initial"
								animate="visible"
								exit="exit"
								transition={{ duration: 0.6, ease: "easeIn" }}
								style={{ width: "100%", height: "auto" }}
							>
								<DayPickerContext.Provider
									value={{ selected, setSelected, nurseCount }}
								>
									<DayPicker
										mode="single"
										required={true}
										locale={{
											...es,
											options: { ...es.options, weekStartsOn: 0 },
										}}
										month={month}
										onMonthChange={setMonth}
										selected={selected}
										onSelect={setSelected}
										hideNavigation={true}
										hideWeekdays
										showOutsideDays
										fixedWeeks
										components={{
											CaptionLabel: CustomCaption,
											Weeks: RowVariant,
										}}
									/>
								</DayPickerContext.Provider>
							</motion.div>
						</AnimatePresence>
					</motion.div>
				)}
			</AnimatePresence>
		</Box>
	);
}

export default memo(Calendar);
